import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface ProductActiveSubstance {
  name: string;
  strength?: string;
}

export interface BarcodeLookupResult {
  product_name: string;
  barcodes: string[];
  pack_size?: string;
  dosage_form?: string;
  active_substances?: ProductActiveSubstance[];
}

export interface DrugNameSuggestion {
  name: string;
  regionCode?: string;
}

export enum ProductCatalogSource {
  CdData = 0,
  Irish = 1,
}

export interface ProductLookupResult {
  catalogSource: ProductCatalogSource;
  product_name: string;
  barcodes: string[];
  pack_size?: string;
  dosage_form?: string;
  active_substances?: ProductActiveSubstance[];
}

export interface ProductSearchRequest {
  pageNumber: number;
  pageSize: number;
  drugNames?: string[];
  manufacturers?: string[];
  catalogSources?: ProductCatalogSource[];
}

export interface PagedProductLookupResult {
  data: ProductLookupResult[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

export interface RegisterMedicationPayload {
  product_name: string;
  barcode: string;
  manufacturer?: string;
  dosage_form?: string;
  active_substances?: string;
  pack_size?: string;
}

export interface RegisterMedicationResult {
  ok: boolean;
  id?: number;
  licence_number?: string;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class ProductsService {
  private url = `${environment.apiUrl}v1/Products`;

  constructor(private http: HttpClient) {}

  lookup(barcode: string): Observable<BarcodeLookupResult | null> {
    return this.http
      .get<{ result?: BarcodeLookupResult | null }>(
        `${this.url}/lookup?barcode=${encodeURIComponent(barcode)}`,
      )
      .pipe(
        map((res) => res?.result ?? null),
        catchError(() => of(null)),
      );
  }

  searchDrugNames(query: string): Observable<DrugNameSuggestion[]> {
    return this.http
      .get<{ result?: DrugNameSuggestion[] }>(
        `${this.url}/drugnames?q=${encodeURIComponent(query)}`,
      )
      .pipe(
        map((res) => res?.result ?? []),
        catchError(() => of([])),
      );
  }

  getManufacturers(): Observable<string[]> {
    return this.http.get<{ result?: string[] }>(`${this.url}/manufacturers`).pipe(
      map((res) => res?.result ?? []),
      catchError(() => of([])),
    );
  }

  getDosageForms(): Observable<string[]> {
    return this.http.get<{ result?: string[] }>(`${this.url}/dosageforms`).pipe(
      map((res) => res?.result ?? []),
      catchError(() => of([])),
    );
  }

  searchProducts(
    request: ProductSearchRequest,
  ): Observable<PagedProductLookupResult> {
    return this.http
      .post<{ result?: PagedProductLookupResult }>(`${this.url}/search`, request)
      .pipe(
        map(
          (res) =>
            res?.result ?? {
              data: [],
              pageNumber: request.pageNumber,
              pageSize: request.pageSize,
              totalRecords: 0,
              totalPages: 0,
            },
        ),
        catchError(() =>
          of({
            data: [],
            pageNumber: request.pageNumber,
            pageSize: request.pageSize,
            totalRecords: 0,
            totalPages: 0,
          }),
        ),
      );
  }

  registerMedication(
    payload: RegisterMedicationPayload,
  ): Observable<RegisterMedicationResult> {
    return this.http
      .post<{ result?: RegisterMedicationResult }>(`${this.url}/register`, payload)
      .pipe(
        map(
          (res) => res?.result ?? { ok: false, error: 'Empty response' },
        ),
        catchError(() => of({ ok: false, error: 'Network error' })),
      );
  }
}
